import { html, View } from 'rune-ts';
import klass from './FundingFormSection.module.scss';
import { font } from '@services/MarppleShop/shared/typography';
import { createProgressParts } from '../../../../../../../../shared/rune-component/Progress';
import {
  FundingHelper,
  FundingStatus,
  FundingStatuses,
} from '@services/MarppleShop/renderApp/components/cells/FundingCard/FundingHelper.ts';
import { HumanReadableFormatter } from '../../../../../../../../shared/utils/formatter/text';
import { doIf } from '@services/MarppleShop/shared/util';
import { ProgressPercentage } from '../../../../../../../../shared/rune-component/Progress/ProgressPercentage.ts';
import { ProgressBar } from '../../../../../../../../shared/rune-component/Progress/ProgressBar.ts';
import {
  ProductTimeHelper,
  ResultDiffDate,
} from '@services/MarppleShop/renderApp/components/cells/ProductCard/utils/ProductTimeHelper.ts';
import {
  ITimerSubscribable,
  TimerSubscriptionManager,
  TimerTick,
} from '@services/MarppleShop/shared/util/timer';
import { commify } from '@modules/Util/S/Function/util';

export interface FundingFormSectionProps {
  total_supporters_count: number;
  total_amount: number;
  goal_amount: number;
  sell_start_at: Date;
  sell_end_at: Date;
  quantity: number;
  order_count: number;
  out_of_stock: boolean;
  now_timestamp: number;
}

export class FundingFormSection extends View<FundingFormSectionProps> implements ITimerSubscribable {
  private readonly status: FundingStatuses;
  private readonly percentage: number;
  private readonly progress: { bar: ProgressBar; percentage: ProgressPercentage; startAnimation: () => void };

  private readonly timerSubscription = new TimerSubscriptionManager(this);

  constructor(props: FundingFormSectionProps) {
    super(props);
    this.status = FundingHelper.status(this.data);
    this.percentage = FundingHelper.percentage(this.data);
    this.progress = createProgressParts({
      start: 0,
      end: this.percentage,
      auto: false,
      font: font('24_bold', '20_bold'),
      klass: `${klass.progress_bar_custom} ${this.status === FundingStatus.FAILED ? klass.progress_bar_failed : ''} ${this.status === 'COMING_SOON' ? klass.progress_percentage_coming_soon : ''}`,
    });
  }

  protected override template() {
    const { bar, percentage } = this.progress;

    return html`
      <section class="${klass.section}">
        <!-- 달성률 -->
        <div class="${klass.top}">
          <div class="${klass.row}">
            <label class="${klass.label} ${font('14_regular', '12_regular')}"
              >${ET('mps2::funding::form::percentage::label')}</label
            >
            <div class="${klass.progress}">
              <div class="${klass.progress_percentage}">
                <div>${percentage}</div>
                <div class="${font('12_medium', '10_medium')}">${this.makeGoalAmountHtml()}</div>
              </div>

              <div class="${klass.progress_bar}">${bar}</div>
            </div>
          </div>
        </div>

        ${doIf(
          () => html`
            <div class="${klass.bottom}">
              <!-- 참여자 수 -->
              <div class="${klass.row}">
                <label class="${klass.label} ${font('14_medium', '12_medium')}"
                  >${ET('mps2::funding::form::supporters::label')}</label
                >
                <span class="${klass.text} ${font('14_medium', '12_medium')}"
                  >${html.preventEscape(
                    ET('mps2::funding::form::supporters::count', {
                      count: commify(this.data.total_supporters_count) as number,
                      font: font('24_bold', '20_bold'),
                    }),
                  )}</span
                >
              </div>

              <!-- 종료까지 -->
              ${doIf(
                () => html`
                  <div class="${klass.row}">
                    <label class="${klass.label} ${font('14_regular', '12_regular')}"
                      >${ET('mps2::funding::form::end::label')}</label
                    >
                    ${this.makeIntervalDateHtml()}
                  </div>
                `,
                this.status === FundingStatus.ING || this.status === FundingStatus.BREAKTHROUGH,
              )}
            </div>
          `,
          Boolean(this.status !== FundingStatus.COMING_SOON && this.data.sell_end_at),
        )}
      </section>
    `;
  }

  protected override onRender() {
    this.timerSubscription.subscribe();
  }

  protected override onUnmount(): void {
    super.onUnmount();
    this.destroy();
  }

  private makeGoalAmountHtml() {
    if (this.status === FundingStatus.FAILED) return html``;

    if (this.status === FundingStatus.SUCCESS)
      return html`<span class="${klass.success}">${ET('preorder::challenge_success')}</span>`;

    if (this.status === FundingStatus.BREAKTHROUGH) {
      const goal = this.data.total_amount;
      const { amount } = HumanReadableFormatter.humanizeToCurrency({ amount: goal, lang: ET.lang });
      return html`<span class="${klass.breakthrough}">
        <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 12 12" fill="none">
          <g clip-path="url(#clip0_3206_125760)">
            <path
              d="M8.53503 4.56633C7.76003 3.92633 6.84003 3.24133 7.37003 1.47633C7.45003 1.21633 7.23003 0.956333 6.96503 1.01133C6.08003 1.19133 4.39003 2.27633 4.30503 4.18633C4.24503 5.53633 4.67003 6.19133 4.96503 7.00633C5.13503 7.47633 4.63003 7.82133 4.19003 7.58133C3.38503 7.14133 3.01503 6.46633 2.90503 5.47133C2.88003 5.22633 2.55003 5.15633 2.44003 5.38133C2.11503 6.06133 1.98003 6.85133 2.11003 7.67633C2.37003 9.40133 3.80503 10.7713 5.53503 10.9713C7.91503 11.2413 9.93003 9.85133 9.93003 7.52633C9.93003 6.72133 9.80503 5.62133 8.53003 4.57133L8.53503 4.56633Z"
              fill="currentColor"
            />
          </g>
          <defs>
            <clipPath id="clip0_3206_125760">
              <rect width="100%" height="100%" fill="white" />
            </clipPath>
          </defs>
        </svg>
        ${html.preventEscape(
          ET('mps2::funding::form::percentage::status::breakthrough', { goal: amount.toLocaleString() }),
        )}</span
      >`;
    }

    if (this.status === FundingStatus.ING || this.status === FundingStatus.COMING_SOON) {
      const goal = this.data.goal_amount - this.data.total_amount;
      const { amount } = HumanReadableFormatter.humanizeToCurrency({ amount: goal, lang: ET.lang });
      return html`<span class="${klass.ing}"
        >${html.preventEscape(
          ET('mps2::funding::form::percentage::status::ing', {
            goal: amount.toLocaleString(),
          }),
        )}</span
      >`;
    }

    return html``;
  }

  private makeIntervalDateHtml() {
    const { day, hour, minute, second } = ProductTimeHelper.diffDate(
      this.data.sell_end_at,
      new Date(this.data.now_timestamp),
    );

    return html`<span
      class="${klass.text} ${font('14_medium', '12_medium')}"
      data-start="${new Date(this.data.now_timestamp)}"
      data-end="${this.data.sell_end_at}"
      data-is-funding
      >${html.preventEscape(this.makeIntervalDateText({ day, hour, minute, second }))}</span
    >`;
  }

  private makeIntervalDateText({ day, hour, minute, second }: ResultDiffDate) {
    if (day)
      return ET('mps2::funding::form::end::remain_day', {
        remain: day,
        font: font('24_bold', '20_bold'),
      });

    return ET('mps2::funding::form::end::remain_time', {
      remain: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`,
      font: font('24_bold', '20_bold'),
    });
  }

  public onTimerTick(event: TimerTick): void {
    const { timestamp } = event.detail;

    const { day, hour, minute, second } = ProductTimeHelper.diffDate(
      this.data.sell_end_at,
      new Date(timestamp),
    );

    if (!(day || hour || minute || second)) setTimeout(() => window.location.reload(), 50);

    const $element = this.element().querySelector(`[data-is-funding]`);
    if ($element) $element.innerHTML = this.makeIntervalDateText({ day, hour, minute, second });
  }

  public destroy(): void {
    this.timerSubscription.unsubscribe();
  }
}
